import moment from 'moment'
import {LineString} from "ol/geom";

function round2 (v: number) {
  return Math.round((v + Number.EPSILON) * 100) / 100
}

function radians (degrees: number) {
  return (degrees % 360) * (Math.PI / 180)
}

function degrees (radians: number) {
  return radians * (180 / Math.PI)
}

function getCoordinatesFromDistanceAndBearing (lat: number, long: number, distance: number, bearing_deg: number) {
  const R = 6373.0 * 1000 // radius of earth
  const lat1 = radians(lat)
  const lon1 = radians(long)
  const a = radians(bearing_deg)
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(distance / R) + Math.cos(lat1) * Math.sin(distance / R) * Math.cos(a))
  const lon2 = lon1 + Math.atan2(
    Math.sin(a) * Math.sin(distance / R) * Math.cos(lat1),
    Math.cos(distance / R) - Math.sin(lat1) * Math.sin(lat2)
  )
  return { latitude: degrees(lat2), longitude: degrees(lon2) }
}

export function getMarkPolygon (longitude: number, latitude: number, windDirection: number) {
  return [
    { latitude, longitude },
    ...[-45, -33.75, -22.5, -11.25, 0, 11.25, 22.5, 33.75, 45].map(v => getCoordinatesFromDistanceAndBearing(
      latitude,
      longitude,
      100,
      windDirection + v
    )),
    { latitude, longitude }
  ]
}

export function getRaceMarkFeature (leagueRace: any) {
  const markCoordinates = getMarkPolygon(
    leagueRace.upwind_mark_longitude,
    leagueRace.upwind_mark_latitude,
    leagueRace.calibration_wind_direction
  ).map(c => ([c.longitude, c.latitude]))
  return {
    type: 'Feature',
    id: 123,
    geometry: {
      type: 'Polygon',
      coordinates: [
        markCoordinates
      ]
    },
    properties: {}
  }
}

export function getStartLineFeature (leagueRace: any) {
  return {
    type: 'Feature',
    id: leagueRace.id,
    geometry: {
      type: 'LineString',
      coordinates: [
        [
          leagueRace.start_finish_longitude1,
          leagueRace.start_finish_latitude1
        ],
        [
          leagueRace.start_finish_longitude2,
          leagueRace.start_finish_latitude2
        ]
      ]
    },
    properties: {}
  }
}

export function extractId (s: string) : number {
  const m = s.match(/([0-9]+)$/)
  if (m !== null) {
    return parseInt(m[0])
  } else {
    return 0
  }
}

export const niceDate = (datetime: Date | string | null | undefined) => datetime ? moment(datetime).format('DD.MM.YYYY') : ''

export const niceMonth = (datetime: Date | string | null | undefined) => datetime ? moment(datetime).format('MMM YYYY') : ''

// Browser-local calendar-day key (YYYY-MM-DD), for grouping sessions by day.
export const niceIsoDate = (datetime: Date | string | null | undefined) => datetime ? moment(datetime).format('YYYY-MM-DD') : ''

export function niceDateTime (datetime: Date | string) : string {
  return moment(String(datetime)).format('DD.MM.YYYY HH:mm')
}

export function round (n: number) : number {
  return round2(n)
}


// Display label for a boat in racing views (map markers, multi-track playback).
// Sail number identifies a keelboat entry best, while dinghy sailors recognise
// each other by skipper name, so 'auto' swaps the first two on the boat class
// category; 'sail_number' / 'skipper' force one first (multi-track playback
// toggle). The boat class/type is the last resort. `sail_number` is free text,
// so it doubles as a name.
export type BoatLabelMode = 'auto' | 'sail_number' | 'skipper'

export function boatLabel(activity: any, mode: BoatLabelMode = 'auto'): string {
  const sailNumber = activity?.upload?.user_boat_details?.sail_number?.trim()
  const skipper = activity?.user_details?.name
  const skipperFirst = mode === 'skipper' || (mode === 'auto' && activity?.boat_details?.category === 'DI')
  return (
    (skipperFirst ? skipper || sailNumber : sailNumber || skipper)
    || activity?.boat_details?.name
    || ''
  )
}

export function truncate(text: string, length: number, suffix: string = '...') : string {
  if (text.length > length) {
    return text.substring(0, length) + suffix
  } else {
    return text
  }
}

export function stripHtml(html: string) : string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

export function msToknots(speed: number, appendUnit = true) : string | Number {
  if (speed !== null) {
    const v = round(speed * 1.943844)
    if(appendUnit){
      // Non-breaking space so the value and unit never wrap apart.
      return `${v}\u00A0kn`
    }else{
      return v
    }
  } else {
    return '-'
  }
}

export type SpeedUnit = 'kn' | 'ms'

export function convertSpeed(ms: number, unit: SpeedUnit): number {
  return unit === 'kn' ? ms * 1.943844 : ms
}

export function speedUnitLabel(unit: SpeedUnit): string {
  return unit === 'kn' ? 'kn' : 'm/s'
}

export function formatSpeed(ms: number | null | undefined, unit: SpeedUnit): string {
  if (ms == null) return '—'
  // Non-breaking space so the value and unit never wrap apart.
  return `${round(convertSpeed(ms, unit))}\u00A0${speedUnitLabel(unit)}`
}

export function niceTime(datetime: Date | string) : string {
  if (datetime !== null) {
    return moment(String(datetime)).format('HH:mm:ss')
  } else {
    return '-'
  }
}

export function mTonm (distance: number) : string {
  if (distance !== null) {
    // Non-breaking space so the value and unit never wrap apart.
    return `${round(distance / 1852)}\u00A0NM`
  } else {
    return '-'
  }
}

export function dateFromDatetime (datetime: Date | string) : string {
  return moment(String(datetime)).format('DD.MM.YYYY')
}

export function timeFromDatetime (datetime: Date | string) : string {
  return moment(String(datetime)).format('HH:mm:ss')
}

export function niceDuration (durationSeconds: number) {
  if (durationSeconds > 0) {
    return new Date(durationSeconds * 1000).toISOString().substr(11, 8)
  } else {
    return '-'
  }
}

export function timeFromTimestamp (ts: number) : string {
  return moment(ts * 1000).format('HH:mm:ss')
}

export function datetimeLocalFromTimestamp (ts: number) : string {
  return moment(ts * 1000).format('YYYY-MM-DDTHH:mm:ss')
}

export function unixFromDatetimeLocal (value: string) : number {
  return moment(value).unix()
}

// Backend datetime fields are timezone-aware and serialize as UTC ISO 8601
// (e.g. "2026-05-19T06:55:42Z"). <input type="datetime-local"> can only hold a
// naive local wall-clock string, so API values must be explicitly converted to
// local time before binding — assigning the raw ISO string directly silently
// drops the timezone and gets reinterpreted as local time, shifting it by the
// browser's UTC offset every time it round-trips through the input.
export function datetimeLocalFromISO (iso: string) : string {
  return moment(iso).format('YYYY-MM-DDTHH:mm:ss')
}

// Inverse of datetimeLocalFromISO: the backend has no per-request timezone
// activation, so a naive datetime-local string PATCHed as-is is interpreted as
// UTC rather than the browser's local time. Convert explicitly before sending.
export function isoFromDatetimeLocal (value: string) : string {
  return moment(value).toISOString()
}

// Multiplicative factor such that corrected_time = elapsed_time * factor, matching
// performance/viewsets.py::_handicap_duration_factor and competitions/scoring.py::_corrected_time
// on the backend — kept in sync with those so client-side playback and server-computed
// leg/segment corrected times agree.
export function handicapDurationFactor (
  system: string | null | undefined,
  rating: number | null | undefined,
  tcfA?: number | null,
  tcfB?: number | null,
) : number | null {
  if (!rating) return null
  if (system === 'DF' || system === 'Division Factor') return 1 / rating
  if (system === 'YS' || system === 'Yardstick') return 100 / rating
  if (system === 'PY' || system === 'Portsmouth Yardstick') return 1000 / rating
  if (system === 'PF' || system === 'PHRF') return (tcfA ?? 650) / ((tcfB ?? 550) + rating)
  return null
}

export function niceDatetimeSeconds (datetime: Date) {
  return moment(String(datetime)).format('DD.MM.YYYY HH:mm:ss')
}

export function durationWithoutMilliseconds (durationString: string) {
  return durationString ? durationString.split('.')[0] : durationString
}

export function niceResult (points: number, raceId: number, item: any) {
  if (item.excluded[`excludablepoints_${raceId}`]) {
    return `(${points})`
  }
  return points
}

export function  getHeadingDiff(h1, h2){
  let diff = h1;
  if (h1 > 180) {
    diff = h1 - 360
  }
  const target = h2 - diff;
  if (target < 180) {
    return Math.abs(target)
  } else {
    return Math.abs(target - 360)
  }
}

// Mean of a set of headings/bearings (degrees), correct across the 0°/360° wrap --
// unlike a plain arithmetic mean (e.g. 350° and 10° would wrongly average to 180°
// instead of ~0°), this averages the unit vectors and takes the resulting angle.
export function circularMeanDeg(valuesDeg: number[]): number | null {
  if (!valuesDeg.length) return null
  let sinSum = 0
  let cosSum = 0
  for (const v of valuesDeg) {
    const rad = v * Math.PI / 180
    sinSum += Math.sin(rad)
    cosSum += Math.cos(rad)
  }
  const meanDeg = Math.atan2(sinSum / valuesDeg.length, cosSum / valuesDeg.length) * 180 / Math.PI
  return (meanDeg + 360) % 360
}


/* todo: migrate filters



Vue.filter('niceDurationWithOffset', function (datetime: Date, offset_seconds: number) {
  if (offset_seconds !== null) {
    return moment(String(datetime)).add(offset_seconds, 'seconds').format('HH:mm:ss')
  } else {
    return '-'
  }
}),
Vue.filter('radTodeg', function (radians: number) {
  return degrees(radians)
}),
Vue.filter('getHeading', function (fromLong: number, fromLat: number, toLong: number, toLat: number) {
  const lat1 = radians(fromLat)
  const lat2 = radians(toLat)
  const dLon = radians(toLong - fromLong)
  const x = Math.sin(dLon) * Math.cos(lat2)
  const y = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)
  return Math.atan2(x, y)
}),
Vue.filter('getDistance', function (fromLong: number, fromLat: number, toLong: number, toLat: number) {
  const lat1 = radians(fromLat)
  const lon1 = radians(fromLong)
  const lat2 = radians(toLat)
  const lon2 = radians(toLong)
  const r = 6373.0 * 1000 // radius of earth
  const dlon = lon2 - lon1
  const dlat = lat2 - lat1
  const a = Math.sin(dlat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlon / 2) ** 2
  const c = 2 * Math.asin(Math.sqrt(a))
  return Math.round(c * r)
}),
Vue.filter('truncate', function (text: string, length: number, suffix: string = '...') {
  if (text.length > length) {
    return text.substring(0, length) + suffix
  } else {
    return text
  }
})
 */

async function uploadChunk(chunk, bytesTransferred, fileSize, url, isUploading: boolean, uploadProgress: any) {
  console.log('uploading chunk...')
  if (!isUploading) {
    return -1
  }
  const start = bytesTransferred
  const end = bytesTransferred + chunk.size - 1
  uploadProgress.value = 100 * start / fileSize;

  try {
    const r = await fetch(url, {
      method: 'PUT',
      body: chunk,
      headers: {
        'Content-Range': `bytes ${start}-${end}/${fileSize}`
      },
    })
    if (r.status === 308) {
      const header = r.headers.get('range')
      const range = header.match(/(\d+?)-(\d+?)$/)
      return parseInt(range[2]) + 1
    } else if (r.status === 200) {
      return -1
    } else {
      throw Error(`Upload chunk ${fileSize} failed.`)
    }
  } catch (e) {
    console.error('in upload chunk', e)
    throw e
  }
}

export async function  uploadFile ({file, presignedUrl, chunksize = 32 * 256 * 1024, uploadProgress} : {file: any, presignedUrl: string, chunksize: number, uploadProgress: any}) {
  // start resumable upload
  console.log('uploading file')
  const isUploading = true;
  const resumableResponse = await fetch(presignedUrl, {
    method: 'POST',
    headers: {
      'x-goog-resumable': 'start'
    },
    credentials: 'omit'
  })
  // upload actual file in chunks
  let bytesTransferred = 0
  while (bytesTransferred >= 0) {
    const chunk = file.slice(bytesTransferred, Math.min(bytesTransferred + chunksize, file.size), file.type)
    bytesTransferred = await uploadChunk(chunk, bytesTransferred, file.size, resumableResponse.headers.get('location'), isUploading, uploadProgress)
  }
  console.log('upload complete')
  return Promise.resolve('ok')
}

/**
 * Smooths an array of speed values using a moving average.
 * @param {number[]} speeds - The array of raw speed values.
 * @param {number} windowSize - The size of the smoothing window (must be odd and >= 1).
 * @returns {number[]} The smoothed speed values.
 */
function smoothSpeeds(speeds, windowSize = 3) {
  if (!Array.isArray(speeds) || speeds.length === 0) {
    return [];
  }

  if (windowSize < 1 || windowSize % 2 === 0) {
    throw new Error('windowSize must be an odd number >= 1');
  }

  const smoothed = [];

  for (let i = 0; i < speeds.length; i++) {
    let sum = 0;
    let count = 0;

    const halfWindow = Math.floor(windowSize / 2);
    const start = Math.max(0, i - halfWindow);
    const end = Math.min(speeds.length - 1, i + halfWindow);

    for (let j = start; j <= end; j++) {
      sum += speeds[j];
      count++;
    }

    smoothed.push(sum / count);
  }

  return smoothed;
}

// minSpeed/maxSpeed override the per-array range — pass them to grade several
// tracks against one shared scale (e.g. the multi-track comparison view).
export function getSpeedColors(speeds: number[], minSpeed?: number, maxSpeed?: number) {
  if (!Array.isArray(speeds) || speeds.length === 0) {
    return [];
  }
  const lo = minSpeed ?? Math.min(...speeds);
  const hi = maxSpeed ?? Math.max(...speeds);
  const range = hi - lo || 1;

  const smoothedSpeeds = smoothSpeeds(speeds);

  return smoothedSpeeds.map(speed => {
    const ratio = Math.min(Math.max((speed - lo) / range, 0), 1);
    let r, g, b = 0;
    if (ratio < 0.5) {
      // Green to Yellow
      r = Math.round(2 * ratio * 255);
      g = 255;
    } else {
      // Yellow to Red
      r = 255;
      g = Math.round(2 * (1 - ratio) * 255);
    }
    const toHex = x => x.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  })
}

export function getSpeedRange(speeds: number[]): { min: number, max: number } | null {
  if (!Array.isArray(speeds) || speeds.length === 0) {
    return null;
  }
  return { min: Math.min(...speeds), max: Math.max(...speeds) };
}

export function getVmcRange(activity: any, vmcReferenceHeading): { min: number, max: number } | null {
  let vmcs = activity.speed_ms?.map((s, i) => s * Math.cos(radians(getHeadingDiff(activity.heading_deg[i], vmcReferenceHeading))))

  if (!Array.isArray(vmcs) || vmcs.length === 0) {
    return null;
  }

  const smoothedVmcs = smoothSpeeds(vmcs);
  return { min: Math.min(...smoothedVmcs), max: Math.max(...smoothedVmcs) };
}

export function getVmcColors(activity: any, vmcReferenceHeading) {
  let vmcs = activity.speed_ms?.map((s, i) => s * Math.cos(radians(getHeadingDiff(activity.heading_deg[i], vmcReferenceHeading))))

  if (!Array.isArray(vmcs) || vmcs.length === 0) {
    return [];
  }

  const smoothedVmcs = smoothSpeeds(vmcs);

  const minVmc = Math.min(...smoothedVmcs);
  const maxVmc = Math.max(...smoothedVmcs);
  const range = maxVmc - minVmc || 1;

  return smoothedVmcs.map(vmc => {
    const ratio = (vmc - minVmc) / range;
    let r, g, b = 0;
    if (ratio < 0.5) {
      // Green to Yellow
      r = Math.round(2 * ratio * 255);
      g = 255;
    } else {
      // Yellow to Red
      r = 255;
      g = Math.round(2 * (1 - ratio) * 255);
    }
    const toHex = x => x.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  })
}


// Initial map height of ActivityMultiPlayer (user can drag-resize from there); shared with
// ActivityMultiPlayerSkeleton so the loading placeholder takes up the same space.
export const MULTI_PLAYER_MAP_HEIGHT = 500

// Course-element types offerable from an "add" control (Map.client.vue's own "+"
// picker, or an external one like the multi-track analysis page's "Edit course" dialog).
export const COURSE_ADD_MODES: { mode: string; label: string; icon: string }[] = [
  { mode: 'gate_start',    label: 'Start line',    icon: 'mdi-flag' },
  { mode: 'gate_start_finish', label: 'Start/finish line', icon: 'mdi-flag-variant' },
  { mode: 'gate_finish',   label: 'Finish line',   icon: 'mdi-flag-checkered' },
  { mode: 'gate',          label: 'Gate',           icon: 'mdi-vector-line' },
  { mode: 'mark',          label: 'Mark',           icon: 'mdi-map-marker' },
  { mode: 'mark_upwind',   label: 'Upwind mark',   icon: 'mdi-map-marker-up' },
  { mode: 'mark_downwind', label: 'Downwind mark', icon: 'mdi-map-marker-down' },
]

// Converts a backend CourseMark (SF start+finish, ST start, FI finish, GA gate, MA/SE mark) into
// the GeoJSON feature shape used by the editable course layer (Map.client.vue's
// drawnFeatures / editableSource).
export function courseMarkToFeature(mark: any): any | null {
  if (mark.type === 'SF' || mark.type === 'ST') {
    return {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[mark.longitude1, mark.latitude1], [mark.longitude2, mark.latitude2]] },
      properties: { featureType: 'gate', gateType: mark.type === 'SF' ? 'start_finish' : 'start' },
    }
  }
  if (mark.type === 'FI') {
    return {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[mark.longitude1, mark.latitude1], [mark.longitude2, mark.latitude2]] },
      properties: { featureType: 'gate', gateType: 'finish' },
    }
  }
  if (mark.type === 'GA') {
    return {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[mark.longitude1, mark.latitude1], [mark.longitude2, mark.latitude2]] },
      properties: { featureType: 'gate', gateType: 'gate' },
    }
  }
  if (mark.type === 'MA' || mark.type === 'SE') {
    return {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [mark.longitude1, mark.latitude1] },
      properties: { featureType: 'mark', markType: markTypeFromMarkerClass(mark.marker_class) },
    }
  }
  return null
}

// Marks as the leg sequence walks them -- mirrors the backend's _course_leg_marks
// (performance/worker.py): a leading start/finish line is also the finish, so unless the
// course already ends on a finish line it's appended as the final leg's end. A course
// that is just the line becomes one leg, out and back through it.
export function courseLegMarks<T extends { type?: string }>(marks: T[]): T[] {
  const first = marks[0]
  if (!first || first.type !== 'SF') return marks
  const last = marks[marks.length - 1]?.type
  if (marks.length > 1 && (last === 'SF' || last === 'FI')) return marks
  return [...marks, first]
}

// Same closing rule as courseLegMarks, for drawn GeoJSON course features (gateType instead
// of a CourseMark type) -- mirrors get_segments_data (performance/viewsets.py).
export function courseLegFeatures(features: any[]): any[] {
  const isLine = (f: any) => f?.geometry?.type === 'LineString'
  const first = features[0]
  if (!first || !isLine(first) || first.properties?.gateType !== 'start_finish') return features
  const last = features[features.length - 1]
  if (features.length > 1 && isLine(last) && ['start_finish', 'finish'].includes(last.properties?.gateType)) return features
  return [...features, first]
}

// Maps a backend CourseMark.marker_class (UP/UO upwind, DW/DO downwind, null/unset generic)
// to the 'upwind' | 'downwind' | 'mark' value used by the editable-feature properties.
function markTypeFromMarkerClass(markerClass: string | undefined | null): 'upwind' | 'downwind' | 'mark' {
  if (['UP', 'UO'].includes(markerClass ?? '')) return 'upwind'
  if (['DW', 'DO'].includes(markerClass ?? '')) return 'downwind'
  return 'mark'
}

// Human-readable label for a drawn course element (gate line or mark point),
// shared by the course-analysis editor and its read-only shared/leg-statistics views.
export function courseFeatureLabel(f: any): string {
  if (f.geometry?.type === 'LineString') {
    const t = f.properties?.gateType
    if (t === 'start') return 'Start gate'
    if (t === 'start_finish') return 'Start/finish gate'
    if (t === 'finish') return 'Finish gate'
    return 'Gate'
  }
  if (f.properties?.markType === 'upwind') return 'Upwind mark'
  if (f.properties?.markType === 'downwind') return 'Downwind mark'
  return 'Mark'
}

export function getLiftedTackColors(activity: any, vmcReferenceHeading, defaultColor) {
  const tackAngles = activity.analysis?.upwind_analysis?.tacking?.tack_angles?.overall
  let threshold = 45
  if (tackAngles && tackAngles.length > 0) {
    const sorted = [...tackAngles].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]
    threshold = median / 2
  }
  const angles = activity.heading_deg.map(h => getHeadingDiff(h, vmcReferenceHeading))
  return angles.map(a => {
    if (a < threshold) {
      return '#00FF00'
    } else if (a < threshold * 2) {
      return '#FF0000'
    }
    return defaultColor
  })
}
